/**
 * Creates the assembler state — called ONCE by the assembler's root component,
 * which provides it through FloorPlanAssemblerStateContext.
 *
 * Same discipline as the room editor's hook: every model update REPLACES
 * `st.rooms` with a structurally-shared list, so committed-change detection is
 * reference identity and versions are cheap to retain. The rooms are unsaved
 * while some room differs from the saved version (`changedAssemblyRooms`, the
 * save's own list), and the saved version starts as the host's rooms, so rooms
 * auto-placed on open read unsaved. Two things differ from the room editor:
 *
 * - The host's `rooms` prop is live: when it changes (a save landed, a room's
 *   geometry was edited elsewhere), incoming rooms are merged in, in place of
 *   the version shown and into every version undo and redo can reach. Each
 *   version keeps what it changed relative to the saved version (placements,
 *   and a room's own geometry after a quarter turn or a true-up) and takes
 *   everything else from the host. The rooms read as saved unless the merge
 *   kept such a change.
 * - A true-up is solved on the latest snapshot outside the updater, so the
 *   solve runs once.
 */
import { type FloorPlanRoom, type RoomPlacement, sameSavedRoom } from '@kaiizen/kai-floor-plan';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  applyAssemblyPlacementInput,
  type AssemblerMode,
  type AssemblerMutableState,
  type AssemblerSelection,
  type AssemblyRoom,
  autoPlaceRooms,
  createAssemblerState,
  resetAssemblerLayout,
  rotateSelectedAssemblyRoom,
  samePlacement,
  sameRoomGeometry,
  selectAssemblyRoom,
  setAssemblerLevel,
  trueUpAssemblerLevel,
  type TrueUpResult,
  withPlacementOf,
} from '../assembler';
import type { InputKind } from '../editor-model';
import type { FloorPlanAssemblerState } from './FloorPlanAssemblerState';
import { useVersionHistory } from './useVersionHistory';

const byId = (rooms: readonly AssemblyRoom[]): Map<string, FloorPlanRoom> => new Map(rooms.map(room => [room.id, room.room]));

/** Whether two versions of a room have the same geometry by reference (the fast path) or by saved value, so a turn and a turn back read as the room they started from. */
const sameSavedGeometry = (room: FloorPlanRoom, other: FloorPlanRoom): boolean =>
  sameRoomGeometry(room, other) || sameSavedRoom(withPlacementOf(room, other), other);

/** Whether two versions of a room save alike: the same placement and the same geometry. */
const sameSavedAssemblyRoom = (room: FloorPlanRoom, other: FloorPlanRoom): boolean => samePlacement(room, other) && sameSavedGeometry(room, other);

/** Rooms whose placement or geometry differs from the saved room of the same id (or that have no saved counterpart). A true-up changes both. */
export function changedAssemblyRooms(rooms: readonly AssemblyRoom[], saved: readonly AssemblyRoom[]): AssemblyRoom[] {
  const savedRooms = byId(saved);
  return rooms.filter(({ id, room }) => {
    const savedRoom = savedRooms.get(id);
    return !savedRoom || !sameSavedAssemblyRoom(room, savedRoom);
  });
}

/** Whether two room lists hold the same rooms, room for room: the host's rooms as they are, nothing changed here. */
const sameAssemblyRooms = (rooms: readonly AssemblyRoom[], other: readonly AssemblyRoom[]): boolean =>
  rooms.length === other.length && rooms.every((assemblyRoom, index) => assemblyRoom.id === other[index].id && assemblyRoom.room === other[index].room);

/**
 * What a room becomes when the host sends a new version of it: kept whole
 * when its own geometry changed here (a quarter turn, a true-up), the
 * incoming room at the placement given here when it was only moved, and the
 * incoming room as is when nothing changed here.
 */
function keepUserEdits(incoming: FloorPlanRoom, working: FloorPlanRoom | undefined, saved: FloorPlanRoom | undefined): FloorPlanRoom {
  if (!working) return incoming;
  if (saved && !sameSavedGeometry(working, saved)) return working;
  return !saved || !samePlacement(working, saved) ? withPlacementOf(incoming, working) : incoming;
}

/**
 * Fold a new host room list into the working list: incoming geometry wins,
 * but a placement the user changed here (differs from what was saved) is
 * carried onto the incoming room, and a room whose own geometry the user
 * changed (a quarter turn, a true-up) is kept as it is. Rooms the host
 * dropped disappear; new ones get auto-placed. Returns `working` itself when
 * nothing changes.
 */
export function mergeHostRooms(working: AssemblyRoom[], incoming: AssemblyRoom[], saved: readonly AssemblyRoom[]): AssemblyRoom[] {
  const workingRooms = byId(working), savedRooms = byId(saved);
  const merged = autoPlaceRooms(incoming.map(assemblyRoom => {
    const room = keepUserEdits(assemblyRoom.room, workingRooms.get(assemblyRoom.id), savedRooms.get(assemblyRoom.id));
    return room === assemblyRoom.room ? assemblyRoom : { ...assemblyRoom, room };
  }));
  return sameAssemblyRooms(merged, working) ? working : merged;
}

/**
 * A version undo or redo can reach, over a new host room list: merged the
 * way the version shown was, with a room the version lacks placed where the
 * version shown placed it, so a room the host adds holds still as you step.
 */
function rebaseVersion(kept: AssemblyRoom[], incoming: AssemblyRoom[], shown: readonly AssemblyRoom[], saved: readonly AssemblyRoom[]): AssemblyRoom[] {
  const keptIds = new Set(kept.map(({ id }) => id)), shownRooms = byId(shown);
  const placedIncoming = incoming.map(assemblyRoom =>
    keptIds.has(assemblyRoom.id) ? assemblyRoom : { ...assemblyRoom, room: shownRooms.get(assemblyRoom.id) ?? assemblyRoom.room }
  );
  return mergeHostRooms(kept, placedIncoming, saved);
}

/** Whether two room lists save as the same rooms: nothing in `rooms` differs from `saved`. */
const sameSavedRooms = (rooms: readonly AssemblyRoom[], saved: readonly AssemblyRoom[]): boolean => changedAssemblyRooms(rooms, saved).length === 0;

/** The first room whose placement or geometry differs between two versions: what an undo/redo step touched. */
function changedRoomBetween(from: readonly AssemblyRoom[], to: readonly AssemblyRoom[]): AssemblerSelection {
  const fromRooms = byId(from);
  const changed = to.find(({ id, room }) => {
    const previous = fromRooms.get(id);
    return !previous || !sameSavedAssemblyRoom(previous, room);
  });
  return changed ? { kind: 'room', id: changed.id } : { kind: 'none' };
}

function cloneAssemblerState(state: AssemblerMutableState): AssemblerMutableState {
  return { ...state, viewport: { scale: state.viewport.scale, offset: { ...state.viewport.offset } } };
}

export function useCreateFloorPlanAssemblerState(
  rooms: AssemblyRoom[],
  readOnly = false,
  snapping = true,
  inputKind: InputKind = 'pointer',
): FloorPlanAssemblerState {
  const [state, setState] = useState<AssemblerMutableState>(() => createAssemblerState(rooms, readOnly, snapping, inputKind));
  const stateRef = useRef(state);
  stateRef.current = state;
  const history = useVersionHistory(state.rooms, { committed: !state.draftRooms, same: sameSavedRooms, saved: rooms });
  const { replaceShown } = history;

  const mutate = useCallback((mutateState: (state: AssemblerMutableState) => void) => {
    setState(previous => {
      const next = cloneAssemblerState(previous);
      mutateState(next);
      return next;
    });
  }, []);

  const savedRoomsRef = useRef(history.saved);
  savedRoomsRef.current = history.saved;
  const lastRoomsProp = useRef(rooms);
  useEffect(() => {
    if (rooms === lastRoomsProp.current) return;
    lastRoomsProp.current = rooms;
    const saved = savedRoomsRef.current;
    const working = stateRef.current.rooms;
    const merged = mergeHostRooms(working, rooms, saved);
    replaceShown(merged, sameAssemblyRooms(merged, rooms) ? merged : rooms, kept => rebaseVersion(kept, rooms, merged, saved));
    if (merged === working) return;
    mutate(next => {
      next.rooms = merged;
      next.draftRooms = null;
      next.drag = null;
      if (next.selection.kind === 'room' && !merged.some(room => room.id === (next.selection as { id: string }).id)) {
        selectAssemblyRoom(next, { kind: 'none' });
      }
    });
  }, [rooms, mutate, replaceShown]);

  useEffect(() => {
    if (readOnly !== stateRef.current.readOnly) {
      mutate(next => {
        next.readOnly = readOnly;
      });
    }
  }, [readOnly, mutate]);

  useEffect(() => {
    if (snapping !== stateRef.current.snapping) {
      mutate(next => {
        next.snapping = snapping;
      });
    }
  }, [snapping, mutate]);

  const select = useCallback((selection: AssemblerSelection) => mutate(next => selectAssemblyRoom(next, selection)), [mutate]);
  const setLevel = useCallback((level: number) => mutate(next => setAssemblerLevel(next, level)), [mutate]);
  const setMode = useCallback((mode: AssemblerMode) =>
    mutate(next => {
      next.mode = mode;
    }), [mutate]);
  const placeSelected = useCallback((patch: Partial<RoomPlacement>) => mutate(next => applyAssemblyPlacementInput(next, patch)), [mutate]);
  const rotateSelected = useCallback((quarterTurns: number) => mutate(next => rotateSelectedAssemblyRoom(next, quarterTurns)), [mutate]);
  const resetLayout = useCallback(() => mutate(next => resetAssemblerLayout(next)), [mutate]);
  const trueUp = useCallback((): TrueUpResult | null => {
    const next = cloneAssemblerState(stateRef.current);
    const result = trueUpAssemblerLevel(next);
    if (result && next.rooms !== stateRef.current.rooms) {
      mutate(state => {
        state.rooms = next.rooms;
      });
    }
    return result;
  }, [mutate]);

  const restore = useCallback((targetRooms: AssemblyRoom[], selection: AssemblerSelection) => {
    mutate(next => {
      next.rooms = targetRooms;
      next.draftRooms = null;
      next.drag = null;
      next.editingKey = null;
      selectAssemblyRoom(next, selection);
    });
  }, [mutate]);

  /** Show an undo or redo step's rooms, selecting the room the step touched. Mid-drag the draft owns the rooms, so neither steps. */
  const { undo: undoStep, redo: redoStep, canUndo, canRedo, isDirty, saved: savedRooms, markSaved } = history;
  const stepTo = useCallback((takeStep: typeof undoStep) => {
    if (stateRef.current.drag || stateRef.current.draftRooms) return;
    const step = takeStep();
    if (step) restore(step.to, changedRoomBetween(step.from, step.to));
  }, [restore]);
  const undo = useCallback(() => stepTo(undoStep), [stepTo, undoStep]);
  const redo = useCallback(() => stepTo(redoStep), [stepTo, redoStep]);

  const changedRooms = useMemo(() => changedAssemblyRooms(state.rooms, savedRooms), [state.rooms, savedRooms]);

  return useMemo(
    () => ({
      st: state,
      mutate,
      select,
      setLevel,
      setMode,
      placeSelected,
      rotateSelected,
      resetLayout,
      trueUp,
      undo,
      redo,
      canUndo,
      canRedo,
      isDirty,
      changedRooms,
      markSaved,
      stRef: stateRef,
    }),
    [
      state,
      mutate,
      select,
      setLevel,
      setMode,
      placeSelected,
      rotateSelected,
      resetLayout,
      trueUp,
      undo,
      redo,
      canUndo,
      canRedo,
      isDirty,
      changedRooms,
      markSaved,
    ],
  );
}
